import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";

export type HealthColor = "green" | "amber" | "red" | "unknown";

export interface IntegrationHealth {
  integration: string;
  configured: boolean;
  last_attempt_at: string | null;
  last_attempt_status: string | null;
  last_success_at: string | null;
  /** Most recent captured error from sync_log (null if no error ever, or
   *  if the most recent error was wiped by a successful sync — the
   *  backend only returns this when there's something useful to show). */
  last_error_summary: string | null;
  last_error_at: string | null;
  failures_24h: number;
  records_failed_24h: number;
  attempts_24h: number;
  status: HealthColor;
  reason: string | null;
}

export interface FailedRecord {
  integration: string;
  target: string;
  error: string | null;
  occurred_at: string | null;
}

export interface SystemHealth {
  status: HealthColor;
  summary: string;
  integrations: IntegrationHealth[];
  failed_records: FailedRecord[];
  checked_at: string;
}

/** Polled every 60s so a sync that breaks mid-session surfaces in the
 * header without a refresh. Cheap query (two indexed selects), so the
 * cost of every-tab polling is negligible compared to the value of
 * "is the data on screen actually fresh?" */
export function useSystemHealth() {
  return useQuery<SystemHealth>({
    queryKey: ["system", "health"],
    queryFn: () => api.get("/system/health").then((r) => r.data),
    refetchInterval: 60_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
  });
}
